<script>
  import { onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import { ui } from '../lib/ui.svelte.js';
  import Select from '../components/Select.svelte';

  let resource = $state('');
  let rows = $state([]);
  let loading = $state(true);
  let err = $state('');
  let selected = $state(new Set()); // audit ids currently checked
  let expanded = $state(new Set()); // ids whose detail row is open
  let lastSelIndex = -1;            // anchor row for shift-click range select

  async function load() {
    loading = true; err = '';
    try {
      const r = await api.listAudit(resource, 200);
      rows = r.audit || [];
      selected = new Set(); // selections are per-result-set
      lastSelIndex = -1;
    } catch (e) { err = e.message; }
    finally { loading = false; }
  }

  onMount(load);

  function fmt(t) { return new Date(t).toLocaleString(); }

  // Row expansion: reveal the full audit detail (the Details map plus the
  // request metadata that the columns don't show) for one entry.
  function toggleExpand(id) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    expanded = next;
  }
  function hasDetails(r) { return r.details && Object.keys(r.details).length > 0; }
  function detailJson(r) { return JSON.stringify(r.details ?? {}, null, 2); }

  // --- Selection -------------------------------------------------------
  // Click toggles one row; shift-click selects the range from the last-clicked
  // row to this one (adds them). onclick (not onchange) so we get shiftKey.
  function toggleRow(index, shiftKey) {
    const next = new Set(selected);
    if (shiftKey && lastSelIndex >= 0) {
      const lo = Math.min(lastSelIndex, index), hi = Math.max(lastSelIndex, index);
      for (let i = lo; i <= hi; i++) next.add(rows[i].id);
    } else {
      const id = rows[index].id;
      if (next.has(id)) next.delete(id); else next.add(id);
    }
    selected = next;
    lastSelIndex = index;
  }
  const allSelected = $derived(rows.length > 0 && rows.every((r) => selected.has(r.id)));
  function toggleAll() {
    selected = allSelected ? new Set() : new Set(rows.map((r) => r.id));
  }

  // --- Download --------------------------------------------------------
  function downloadRows(list, label) {
    if (!list.length) return;
    const blob = new Blob([JSON.stringify(list, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url;
    a.download = `audit-${label}-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    ui.snack(`Downloaded ${list.length.toLocaleString()} entr${list.length === 1 ? 'y' : 'ies'}`, 'success');
  }
  function downloadAll() { downloadRows(rows, 'all'); }
  function downloadSelected() {
    downloadRows(rows.filter((r) => selected.has(r.id)), 'selected');
  }
</script>

<!-- Toolbar: Refresh sits on the same row as the Resource filter; the two
     download actions sit on the row beneath. Reads cleanly on a narrow phone
     instead of three full-width pills stacked above the filter. -->
<div class="audit-toolbar">
  <button
    onclick={load}
    disabled={loading}
    class="audit-tb-refresh px-4 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50 whitespace-nowrap"
    title="Reload the audit log"
  >
    <span class="material-symbols-outlined text-base align-middle">refresh</span>
    Refresh
  </button>
  <div class="audit-tb-filter">
    <Select
      label="Resource"
      bind:value={resource}
      onchange={load}
      options={[
        { value: '',        label: 'All resources' },
        { value: 'bucket',  label: 'Buckets' },
        { value: 'capture', label: 'Captures' },
        { value: 'user',    label: 'Users' },
        { value: 'status_target', label: 'Status targets' },
      ]}
    />
  </div>
  <div class="audit-tb-downloads">
    <button
      onclick={downloadSelected}
      disabled={selected.size === 0}
      class="px-4 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50"
      title="Download the selected entries as JSON"
    >
      <span class="material-symbols-outlined text-base align-middle">download</span>
      Download selected{selected.size ? ` (${selected.size})` : ''}
    </button>
    <button
      onclick={downloadAll}
      disabled={rows.length === 0}
      class="px-4 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50"
      title="Download all loaded entries as JSON"
    >
      <span class="material-symbols-outlined text-base align-middle">download</span>
      Download all
    </button>
  </div>
</div>

{#if loading}
  <p class="text-on-surface-variant">Loading...</p>
{:else if err}
  <p class="text-error">{err}</p>
{:else if rows.length === 0}
  <div class="bg-surface-container rounded-2xl p-12 text-center text-on-surface-variant">
    No audit entries yet.
  </div>
{:else}
  <div class="bg-surface-container rounded-2xl overflow-auto audit-scroll">
    <table class="w-full text-sm min-w-[680px]">
      <thead class="text-on-surface-variant text-xs uppercase tracking-wide audit-thead">
        <tr class="border-b border-outline-variant">
          <th class="px-4 py-3 w-10">
            <input type="checkbox" class="audit-check" checked={allSelected} onchange={toggleAll} aria-label="Select all" />
          </th>
          <th class="px-2 py-3 w-8" aria-hidden="true"></th>
          <th class="text-left px-4 py-3 whitespace-nowrap">When</th>
          <th class="text-left px-4 py-3 whitespace-nowrap">User</th>
          <th class="text-left px-4 py-3 whitespace-nowrap">Action</th>
          <th class="text-left px-4 py-3 whitespace-nowrap">Resource</th>
          <th class="text-left px-4 py-3 whitespace-nowrap">From</th>
        </tr>
      </thead>
      <tbody>
        {#each rows as r, i (r.id)}
          {@const open = expanded.has(r.id)}
          <tr class="border-b border-outline-variant/50 hover:bg-surface-container-high audit-row" class:audit-row-selected={selected.has(r.id)} onclick={() => toggleExpand(r.id)}>
            <td class="px-4 py-3">
              <input type="checkbox" class="audit-check" checked={selected.has(r.id)} onclick={(e) => { e.stopPropagation(); toggleRow(i, e.shiftKey); }} aria-label="Select entry" />
            </td>
            <td class="px-2 py-3 text-center">
              <span class="material-symbols-outlined audit-chevron" style:transform={open ? 'rotate(90deg)' : ''} aria-hidden="true">chevron_right</span>
            </td>
            <td class="px-4 py-3 text-on-surface-variant whitespace-nowrap">{fmt(r.created_at)}</td>
            <td class="px-4 py-3 whitespace-nowrap">{r.username || r.email || (r.user_id ? '#' + r.user_id : '—')}</td>
            <td class="px-4 py-3 font-mono text-xs whitespace-nowrap">{r.action}</td>
            <td class="px-4 py-3 text-on-surface-variant whitespace-nowrap">{r.resource} <span class="font-mono text-xs">{r.resource_id}</span></td>
            <td class="px-4 py-3 text-on-surface-variant whitespace-nowrap">{r.remote_addr}</td>
          </tr>
          {#if open}
            <tr class="audit-detail-row">
              <td colspan="7" class="px-4 py-3">
                <dl class="audit-detail-meta">
                  <div><dt>Action</dt><dd class="font-mono">{r.action}</dd></div>
                  <div><dt>Resource</dt><dd class="font-mono">{r.resource}{r.resource_id ? ' / ' + r.resource_id : ''}</dd></div>
                  <div><dt>User</dt><dd>{r.username || '—'}{r.email ? ` <${r.email}>` : ''}{r.user_id ? ` (#${r.user_id})` : ''}</dd></div>
                  <div><dt>From</dt><dd class="font-mono">{r.remote_addr || '—'}</dd></div>
                  {#if r.request_id}<div><dt>Request ID</dt><dd class="font-mono">{r.request_id}</dd></div>{/if}
                  {#if r.user_agent}<div><dt>User-agent</dt><dd>{r.user_agent}</dd></div>{/if}
                </dl>
                {#if hasDetails(r)}
                  <h4 class="audit-detail-h">Details</h4>
                  <pre class="audit-detail-json">{detailJson(r)}</pre>
                {:else}
                  <p class="text-xs text-on-surface-variant mt-2">No extra detail recorded for this entry.</p>
                {/if}
              </td>
            </tr>
          {/if}
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style>
  /* Toolbar: Refresh + Resource filter share the top row; the download
     buttons wrap onto the row beneath. On a phone the filter takes the
     remaining width next to Refresh instead of everything stacking. */
  .audit-toolbar {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.5rem 0.75rem;
    align-items: center;
    margin-block-end: 1rem;
  }
  .audit-tb-downloads {
    grid-column: 1 / -1;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  /* Wider screens: keep the compact two-row shape but stop the filter from
     sprawling across the whole width. */
  @media (min-width: 640px) {
    .audit-toolbar { grid-template-columns: auto 18rem; justify-content: start; }
  }

  /* Bound the table card height so the rows scroll inside it while the
     toolbar above stays put. Sticky header keeps the column labels +
     select-all checkbox visible while scrolling. */
  .audit-scroll { max-block-size: calc(100dvh - 200px); }
  .audit-thead { position: sticky; inset-block-start: 0; z-index: 1; }
  .audit-thead tr { background: rgb(var(--md-sys-color-surface-container)); }
  .audit-row-selected { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .audit-row { cursor: pointer; }
  .audit-check {
    inline-size: 16px;
    block-size: 16px;
    accent-color: rgb(var(--md-sys-color-primary));
    cursor: pointer;
  }
  .audit-chevron {
    font-size: 18px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    transition: transform 150ms;
  }
  .audit-detail-row { background: rgb(var(--md-sys-color-surface)); }
  .audit-detail-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 0.25rem 1rem;
    margin: 0;
    font-size: 12px;
  }
  .audit-detail-meta div { display: grid; grid-template-columns: 96px 1fr; gap: 0.5rem; align-items: baseline; }
  .audit-detail-meta dt { color: rgb(var(--md-sys-color-on-surface-variant)); }
  .audit-detail-meta dd { margin: 0; word-break: break-all; }
  .audit-detail-h {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin: 0.75rem 0 0.375rem;
  }
  .audit-detail-json {
    margin: 0;
    padding: 0.625rem 0.75rem;
    background: rgb(var(--md-sys-color-surface-container));
    border-radius: 8px;
    font-family: 'Menlo','Consolas',monospace;
    font-size: 12px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    max-block-size: 360px;
    overflow: auto;
  }
</style>
